import { useState, useRef, useEffect } from "react";
import { MoreHorizontal, ExternalLink, Stethoscope, PhoneCall, Pencil, BookOpen, EyeOff, ArrowUpRight } from "lucide-react";
import { T, TONES, GRID, PRODUCTS, FOCUS, ddmon } from "./clientsTokens";
import { OverseasBadge } from "../OverseasBadge";

const HEALTH_TONE = { healthy: "success", watch: "warning", at_risk: "overdue", not_scored: "pale" };

const Tag = ({ children, fill, border, ink }) => (
  <span className="whitespace-nowrap" style={{
    padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
    textTransform: "uppercase", background: fill, border: `1px solid ${border}`, color: ink,
  }}>{children}</span>
);

const RowMenu = ({ c, onAction, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    window.addEventListener("scroll", onClose, true);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
      window.removeEventListener("scroll", onClose, true);
    };
  }, [onClose]);

  const item = (key, label, Icon, ink = T.ink, hint = null) => (
    <button key={key} onClick={() => { onClose(); onAction(key, c); }} data-testid={`client-menu-${key}`}
            className="w-full flex items-start gap-2 text-left px-2 py-1.5 rounded-lg hover:bg-[#F4F2ED]">
      <Icon className="w-[15px] h-[15px] mt-[1px] shrink-0" style={{ color: ink === T.ink ? T.ink3 : ink }} />
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold whitespace-nowrap" style={{ color: ink }}>{label}</span>
        {hint && <span className="block text-[11.5px] font-normal leading-[1.45]" style={{ color: T.ink2 }}>{hint}</span>}
      </span>
    </button>
  );

  return (
    <div ref={ref} className="absolute right-0 top-8 p-1.5" data-testid={`client-menu-${c.client_id}`}
         style={{ width: 262, zIndex: 30, background: T.panel, border: `1px solid ${T.border}`,
                  borderRadius: 12, boxShadow: "0 12px 32px rgba(26,26,26,0.16)" }}>
      {item("open", "Open client", ArrowUpRight)}
      {item("health", "Run a Health Check", Stethoscope)}
      {item("touch", "Log a touch", PhoneCall)}
      {item("edit", "Edit client", Pencil)}
      {item("runbook", "Open runbook", BookOpen)}
      {item("jira", "Open in Jira", ExternalLink)}
      <div className="my-1 h-px" style={{ background: T.dividerInner }} />
      {item("inactive", c.active === false ? "Mark active" : "Mark inactive", EyeOff, T.destructive,
            c.active === false
              ? "Returns the client to the register and restarts check reminders."
              : "Hides the client from the register and stops check reminders. Reversible from Show inactive.")}
    </div>
  );
};

export const ClientRow = ({ c, height, selected, onSelect, onOpen, onAction, menuOpen, setMenu, showInactive }) => {
  const [hover, setHover] = useState(false);
  const overdue = c.check_band === "overdue";
  const tone = TONES[HEALTH_TONE[c.health_band] || "pale"];
  const score = c.last_hc_score ?? c.last_health_score;
  const outOf = c.last_hc_score != null ? 100 : 10;
  const low = score !== null && score !== undefined && score <= (outOf === 100 ? 59 : 6);
  const inactive = c.active === false;

  const spine = overdue ? `inset 3px 0 0 ${T.overdueSpine}`
    : selected ? `inset 3px 0 0 ${T.accent}` : "none";
  const bg = selected ? T.accentTint : hover ? T.accentWash : T.card;

  const counts = [
    c.notes_count ? { t: `${c.notes_count} ${c.notes_count === 1 ? "note" : "notes"}`, work: false } : null,
    c.meetings_count ? { t: `${c.meetings_count} ${c.meetings_count === 1 ? "mtg" : "mtgs"}`, work: false } : null,
    c.open_todo_count ? { t: `${c.open_todo_count} open`, work: true } : null,
  ].filter(Boolean);

  return (
    <div className="grid items-center relative" data-testid={`client-row-${c.client_id}`}
         onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
         style={{ gridTemplateColumns: GRID, columnGap: 10, padding: "6px 12px", minHeight: height,
                  background: bg, boxShadow: spine, borderBottom: `1px solid ${T.dividerRow}`,
                  transition: "background 150ms ease-out" }}>
      <input type="checkbox" checked={selected} onChange={() => onSelect(c.client_id)}
             data-testid={`client-check-${c.client_id}`}
             className={`w-3.5 h-3.5 accent-[#1A1A1A] cursor-pointer ${FOCUS}`} />

      {/* Client identity */}
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <button onClick={() => onOpen(c)} data-testid={`client-open-${c.client_id}`}
                  className="text-[13px] font-semibold truncate hover:underline text-left"
                  style={{ color: inactive ? T.ink2 : T.ink }}>{c.company_name || c.client_id}</button>
      <OverseasBadge currency={c.currency} country={c.country} testid={`client-overseas-${c.client_id}`} />
          {c.is_vip && <Tag fill="#F1EEE6" border={T.border} ink={T.ink2}>VIP</Tag>}
          {inactive && showInactive && <Tag fill={T.panel} border="#E2DED4" ink="#8F8A7F">Inactive</Tag>}
        </div>
        <div className="mono text-[11px] truncate" style={{ color: T.ink2 }}>
          {[c.client_id, c.jira_org_name].filter(Boolean).join(" · ") || "—"}
        </div>
      </div>

      {/* Entitlements */}
      <div className="flex flex-wrap" style={{ gap: 4 }}>
        {(c.entitlements || []).length === 0
          ? <span className="text-[11px]" style={{ color: T.disabled }}>—</span>
          : (c.entitlements || []).map((e) => (
            <span key={e} className="mono whitespace-nowrap" style={{
              flex: "0 0 auto", fontSize: 10, fontWeight: 500, letterSpacing: "0.04em",
              color: T.ink2, background: T.inset, border: `1px solid ${T.dividerInner}`,
              borderRadius: 4, padding: "1px 5px" }}>{e}</span>
          ))}
      </div>

      {/* Health */}
      <div className="flex items-center justify-end gap-1.5">
        {score === null || score === undefined
          ? <span className="mono text-[11.5px]" style={{ color: T.disabled }}>—</span>
          : <>
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: tone.dot }} />
            <span className="mono text-[11.5px] whitespace-nowrap"
                  style={{ color: low ? "#9A3200" : T.ink, fontWeight: low ? 700 : 500 }}>{score}/{outOf}</span>
          </>}
      </div>

      {/* Activity */}
      <div className="mono text-[11.5px] text-right whitespace-nowrap" style={{ color: T.ink2 }}>
        {ddmon(c.last_activity_date) || <span style={{ color: T.disabled }}>—</span>}
      </div>

      {/* Last check + sentiment */}
      <div className="text-right">
        <div className="mono text-[11.5px] whitespace-nowrap" style={{ color: c.last_check_date ? T.ink2 : T.disabled }}>
          {ddmon(c.last_check_date) || "—"}
        </div>
        {c.last_sentiment && (c.last_sentiment === "negative" ? (
          <span className="inline-flex items-center gap-1 mt-0.5 whitespace-nowrap"
                style={{ padding: "1px 8px", borderRadius: 999, fontSize: 10.5, fontWeight: 700,
                         background: TONES.overdue.fill, color: TONES.overdue.ink,
                         border: `1px solid ${TONES.overdue.border}` }}>
            <span className="w-[5px] h-[5px] rounded-full" style={{ background: TONES.overdue.dot }} />
            Negative
          </span>
        ) : (
          <div className="text-[11.5px] capitalize" style={{ color: T.ink2 }}>{c.last_sentiment}</div>
        ))}
      </div>

      {/* Next due */}
      <div className="text-right">
        {c.next_check_date ? (
          <>
            <div className="mono text-[11.5px] whitespace-nowrap"
                 style={{ color: overdue ? "#9A3200" : T.ink, fontWeight: 500 }}>{ddmon(c.next_check_date)}</div>
            {overdue && <div className="mono text-[10px]" style={{ color: "#9A3200", fontWeight: 500 }}>+{c.days_late}d late</div>}
          </>
        ) : (
          <>
            <div className="mono text-[11.5px]" style={{ color: T.disabled }}>—</div>
            {c.last_check_date && <div className="text-[11px]" style={{ color: T.ink2 }}>Not scheduled</div>}
          </>
        )}
      </div>

      {/* Notes & actions */}
      <div className="min-w-0">
        {counts.length === 0 && !c.last_meeting_title
          ? <span className="text-[11px]" style={{ color: T.disabled }}>—</span>
          : <>
            <div className="flex items-center flex-wrap" style={{ gap: 6 }}>
              {counts.map((k) => (
                <span key={k.t} className="mono text-[11px] whitespace-nowrap"
                      style={k.work
                        ? { color: T.ink, fontWeight: 700, background: TONES.warning.fill,
                            border: `1px solid ${TONES.warning.border}`, borderRadius: 4, padding: "0 5px" }
                        : { color: T.ink2 }}>{k.t}</span>
              ))}
            </div>
            {c.last_meeting_title && (
              <div className="text-[11.5px] truncate" style={{ color: T.ink2 }}>{c.last_meeting_title}</div>
            )}
          </>}
      </div>

      {/* Row menu */}
      <div className="relative flex justify-end">
        <button onClick={() => setMenu(menuOpen ? null : c.client_id)} data-testid={`client-dots-${c.client_id}`}
                className={`w-[30px] h-[30px] rounded-[7px] flex items-center justify-center hover:bg-[#F4F2ED] ${FOCUS}`}>
          <MoreHorizontal className="w-4 h-4" style={{ color: T.ink3 }} />
        </button>
        {menuOpen && <RowMenu c={c} onAction={onAction} onClose={() => setMenu(null)} />}
      </div>
    </div>
  );
};

export const ENTITLEMENT_ORDER = ["SLA", "PORTAL", ...PRODUCTS.map((p) => p.key)];
